import { createApiAction } from '@app/packages/api/utils'
import type { V3EmailRequestCause, V3PhoneRequestPurpose } from '@app/packages/api/v3/types/lib'
import type {
  V3PostAuthExchangeParams,
  V3PostAuthLoginParams,
  V3PostAuthPasskeyBeginParams,
  V3PostAuthPasskeyCompleteParams,
  V3PostAuthTestLoginParams,
} from '@app/packages/api/v3/types/params'
import type {
  V3GetAuthPasskeysResponse,
  V3GetAuthPhoneDeliveryMethodsResponse,
  V3GetAuthSessionsResponse,
  V3GetAuthYandexConfigResponse,
  V3PostAuthEmailRequestResponse,
  V3PostAuthExchangeResponse,
  V3PostAuthLoginResponse,
  V3PostAuthPasskeyBeginResponse,
  V3PostAuthPasskeyCompleteResponse,
  V3PostAuthPhoneBindResponse,
  V3PostAuthPhoneRequestResponse,
  V3PostAuthRefreshResponse,
  V3PostAuthTestLoginResponse,
  V3PostAuthYandexBindResponse,
} from '@app/packages/api/v3/types/responses'

export const postAuthExchange = ({ access_token, client_id, client_type, device_id }: V3PostAuthExchangeParams) =>
  createApiAction('postAuthExchange', dispatch =>
    dispatch<V3PostAuthExchangeResponse>({
      endpoint: '/api/v3/auth/exchange',
      method: 'POST',
      credentials: 'same-origin',
      body: JSON.stringify({
        data: {
          type: 'sessions',
          attributes: { access_token, client_id, client_type, device_id },
        },
      }),
    })
  )

export const postAuthTestLogin = ({ user_id, client_id, client_type, device_id }: V3PostAuthTestLoginParams) =>
  createApiAction('postAuthTestLogin', dispatch =>
    dispatch<V3PostAuthTestLoginResponse>({
      endpoint: '/api/v3/auth/test_login',
      method: 'POST',
      credentials: 'same-origin',
      body: JSON.stringify({
        data: {
          type: 'sessions',
          attributes: { user_id, client_id, client_type, device_id },
        },
      }),
    })
  )

/** `refresh_token` names which session to spend; without it the cookie decides, as the web relies on. */
export const postAuthRefresh = (refresh_token?: string) =>
  createApiAction('postAuthRefresh', dispatch =>
    dispatch<V3PostAuthRefreshResponse>({
      endpoint: '/api/v3/auth/refresh',
      method: 'POST',
      credentials: 'same-origin',
      body: JSON.stringify({ data: { type: 'sessions', attributes: refresh_token ? { refresh_token } : {} } }),
    })
  )

export const postAuthLogin = (params: V3PostAuthLoginParams) =>
  createApiAction('postAuthLogin', dispatch =>
    dispatch<V3PostAuthLoginResponse>({
      endpoint: '/api/v3/auth/login',
      method: 'POST',
      credentials: 'same-origin',
      body: JSON.stringify({ data: { type: 'sessions', attributes: params } }),
    })
  )

export const getAuthPhoneDeliveryMethods = (phone: string) =>
  createApiAction('getAuthPhoneDeliveryMethods', dispatch =>
    dispatch<V3GetAuthPhoneDeliveryMethodsResponse>({
      endpoint: `/api/v3/auth/phone/delivery_methods?${buildQuery({ 'filter[phone]': phone })}`,
      method: 'GET',
    })
  )

export const postAuthPhoneRequest = (phone: string, purpose: V3PhoneRequestPurpose) =>
  createApiAction('postAuthPhoneRequest', dispatch =>
    dispatch<V3PostAuthPhoneRequestResponse>({
      endpoint: '/api/v3/auth/phone/request',
      method: 'POST',
      body: JSON.stringify({ data: { type: 'phone_verifications', attributes: { phone, purpose } } }),
    })
  )

export const postAuthPhoneBind = (params: { phone: string; code: string } | { session_id: string; verify_token: string; fingerprint_hash?: string | null }) =>
  createApiAction('postAuthPhoneBind', dispatch =>
    dispatch<V3PostAuthPhoneBindResponse>({
      endpoint: '/api/v3/auth/phone/bind',
      method: 'POST',
      body: JSON.stringify({ data: { type: 'phone_verifications', attributes: params } }),
    })
  )

export const getAuthYandexConfig = () =>
  createApiAction('getAuthYandexConfig', dispatch =>
    dispatch<V3GetAuthYandexConfigResponse>({
      endpoint: '/api/v3/auth/yandex/config',
      method: 'GET',
    })
  )

export const postAuthYandexBind = (params: { code: string; code_verifier?: string } | { access_token: string }) =>
  createApiAction('postAuthYandexBind', dispatch =>
    dispatch<V3PostAuthYandexBindResponse>({
      endpoint: '/api/v3/auth/yandex/bind',
      method: 'POST',
      body: JSON.stringify({ data: { type: 'identities', attributes: params } }),
    })
  )

export const deleteAuthYandexBind = () =>
  createApiAction('deleteAuthYandexBind', dispatch =>
    dispatch<void>({
      endpoint: '/api/v3/auth/yandex/bind',
      method: 'DELETE',
    })
  )

export const postAuthEmailRequest = ({ email, cause }: { email: string; cause: V3EmailRequestCause }) =>
  createApiAction('postAuthEmailRequest', dispatch =>
    dispatch<V3PostAuthEmailRequestResponse>({
      endpoint: '/api/v3/auth/email/request',
      method: 'POST',
      body: JSON.stringify({ data: { type: 'email_verifications', attributes: { email, cause } } }),
    })
  )

export const postAuthPasskeyBegin = (params: V3PostAuthPasskeyBeginParams) =>
  createApiAction('postAuthPasskeyBegin', dispatch =>
    dispatch<V3PostAuthPasskeyBeginResponse>({
      endpoint: '/api/v3/auth/passkey/begin',
      method: 'POST',
      body: JSON.stringify({ data: { type: 'passkey-challenges', attributes: params } }),
    })
  )

export const postAuthPasskeyComplete = (params: V3PostAuthPasskeyCompleteParams) =>
  createApiAction('postAuthPasskeyComplete', dispatch =>
    dispatch<V3PostAuthPasskeyCompleteResponse>({
      endpoint: '/api/v3/auth/passkey/complete',
      method: 'POST',
      body: JSON.stringify({ data: { type: 'passkeys', attributes: params } }),
    })
  )

export const getAuthPasskeys = () =>
  createApiAction('getAuthPasskeys', dispatch =>
    dispatch<V3GetAuthPasskeysResponse>({
      endpoint: '/api/v3/auth/passkeys',
      method: 'GET',
    })
  )

export const deleteAuthPasskeyById = (id: string) =>
  createApiAction('deleteAuthPasskeyById', dispatch =>
    dispatch<void>({
      endpoint: `/api/v3/auth/passkeys/${encodeURIComponent(id)}`,
      method: 'DELETE',
    })
  )

export const getAuthSessions = () =>
  createApiAction('getAuthSessions', dispatch =>
    dispatch<V3GetAuthSessionsResponse>({
      endpoint: '/api/v3/auth/sessions',
      method: 'GET',
    })
  )

export const deleteAuthSessionsCurrent = () =>
  createApiAction('deleteAuthSessionsCurrent', dispatch =>
    dispatch<void>({
      endpoint: '/api/v3/auth/sessions/current',
      method: 'DELETE',
    })
  )

export const deleteAuthSessionsAllExceptCurrent = () =>
  createApiAction('deleteAuthSessionsAllExceptCurrent', dispatch =>
    dispatch<void>({
      endpoint: '/api/v3/auth/sessions/all-except-current',
      method: 'DELETE',
    })
  )

export const deleteAuthSessionById = (id: string) =>
  createApiAction('deleteAuthSessionById', dispatch =>
    dispatch<void>({
      endpoint: `/api/v3/auth/sessions/${encodeURIComponent(id)}`,
      method: 'DELETE',
    })
  )

const buildQuery = (params: Record<string, string>): string => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) search.set(key, value)
  return search.toString()
}
